import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TSpaceBase } from '@onesource/schemas';
import { MdCheck, MdArrowForward } from 'react-icons/md';
import { useNavigate } from '@tanstack/react-router';

interface ModuleCardProps {
  module: TSpaceBase;
  parentSpaceId: string;
  index: number;
  isSelected: boolean;
  onClick: () => void;
}

/**
 * Module Card Component
 * Displays a single module in the left panel list
 * Based on Figma design
 */
export const ModuleCard = ({ module, parentSpaceId, index, isSelected, onClick }: ModuleCardProps) => {
  const navigate = useNavigate();

  const handleSeeMore = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate({ to: `/community-hub/spaces/${parentSpaceId}/modules/${module.id}` });
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        cursor: 'pointer',
        bgcolor: isSelected ? 'action.selected' : 'transparent',
        borderRadius: 1,
        transition: 'background-color 0.2s',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {/* Number Badge */}
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: 2,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: 'grey.400',
          }}
        >
          {index}
        </Typography>
      </Box>

      {/* Module Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'bold',
            mb: 0.5,
          }}
        >
          {module.name}
        </Typography>
        {module.lessonCount !== undefined && module.lessonCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <MdCheck size={12} color="#4caf50" />
            <Typography variant="caption" color="text.secondary">
              {module.lessonCount === 1 ? '1 Lesson' : `${module.lessonCount} Lessons`}
            </Typography>
          </Box>
        )}
        {module.hasDownloadableAssets && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MdCheck size={12} color="#4caf50" />
            <Typography variant="caption" color="text.secondary">
              Downloadable assets
            </Typography>
          </Box>
        )}
      </Box>

      {/* See more link */}
      <Box
        onClick={handleSeeMore}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          color: 'primary.main',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        <Typography variant="caption" color="inherit">
          See more
        </Typography>
        <MdArrowForward size={12} />
      </Box>
    </Box>
  );
};
